import { useEffect, useRef } from 'react';
import { useLocalStorage } from 'usehooks-ts';

import { tutorialActions } from '@app/store/slices/tutorial/tutorialSlice';
import { useAppDispatch, useAppSelector } from '@app/store/hooks';
import {
  useGetCurrentUserProfileQuery,
  useGetCurrentUserQuery,
} from '@app/store/slices/users/usersApi';
import { TUTORIAL_VERSION, TutorialState } from '@app/store/slices/tutorial/tutorialTypes';
import { LOCAL_STORAGE, TUTORIAL_PENDING_KEY } from '@app/shared/constants/common';

export function useTutorialPersist() {
  const dispatch = useAppDispatch();
  const tutorial = useAppSelector((s) => s.tutorial);

  const { data: user, isSuccess } = useGetCurrentUserQuery();
  const userId = user?.id;
  const { data: userProfile, isLoading: isUserProfileLoading } = useGetCurrentUserProfileQuery();

  const storageKey = userId ? LOCAL_STORAGE.tutorialKey(userId) : TUTORIAL_PENDING_KEY;

  const [, setSaved] = useLocalStorage<TutorialState | null>(storageKey, null);

  const hydratedKeyRef = useRef<string | null>(null);
  const skipPersistOnceRef = useRef(false);

  useEffect(() => {
    if (!isSuccess || !userId || !(!isUserProfileLoading && userProfile)) {
      return;
    }
    if (hydratedKeyRef.current === storageKey) {
      return;
    }

    let persisted: TutorialState = {
      enabled: true,
      step: 'HOME_STONE_1',
      awaitReturn: null,
      version: TUTORIAL_VERSION,
      complete: false,
      manual: false,
    };
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        persisted = JSON.parse(raw) as TutorialState;
      }
    } catch {
      // nop
    }
    hydratedKeyRef.current = storageKey;
    skipPersistOnceRef.current = true;
    persisted.complete = userProfile.isTutorialCompleted;
    persisted.enabled = !userProfile.isTutorialCompleted;

    dispatch(tutorialActions.hydrate(persisted));
  }, [dispatch, isSuccess, isUserProfileLoading, userProfile, userId, storageKey]);

  const prevRef = useRef('');
  useEffect(() => {
    if (!isSuccess || !userId || !(!isUserProfileLoading && userProfile)) {
      return;
    }
    if (hydratedKeyRef.current !== storageKey) {
      return;
    }

    if (skipPersistOnceRef.current) {
      skipPersistOnceRef.current = false;
      prevRef.current = JSON.stringify(tutorial);
      return;
    }

    const next = JSON.stringify(tutorial);
    if (next === prevRef.current) {
      return;
    }

    prevRef.current = next;
    setSaved(tutorial);
  }, [tutorial, setSaved, isUserProfileLoading, userProfile, isSuccess, userId, storageKey]);
}
